import { useEffect, useState } from 'react';
import { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useDiscountInfo } from '../discount-table/useDiscountInfo';
import { DiscountScaleProgressProps } from './DiscountScaleProgress.types';

export const useDiscountScaleProgress = (
  {
    discountScales: discount,
    quantity,
    anyMilestoneShowingCheckSV: anyMilestoneShowingCheckSVParent,
    isMobile,
  }: DiscountScaleProgressProps,
  discountMilestoneOffsets: {
    desktop: {
      x: number;
      y: number;
    };
    mobile: {
      x: number;
      y: number;
    };
  },
) => {
  const steps = discount.length;
  const progressSV = useSharedValue(0);
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [lastReachedMilestone, setLastReachedMilestone] = useState<number | null>(-1);
  const { progress } = useDiscountInfo(quantity, discount);
  const anyMilestoneShowingCheckSV = anyMilestoneShowingCheckSVParent ?? useSharedValue(false);

  useEffect(() => {
    progressSV.value = withTiming(progress, {
      duration: 1000,
      easing: Easing.quad,
    });
    discount.forEach((item, i) => {
      if (i === 0) return;
      const left = (i / (steps - 1)) * 100;
      const milestoneReachedNow = progress >= left;
      const milestoneNotAnimatedYet = i > (lastReachedMilestone ?? -1);
      if (milestoneReachedNow && milestoneNotAnimatedYet) {
        setLastReachedMilestone(i);
      } else if (!milestoneReachedNow && !milestoneNotAnimatedYet) {
        setLastReachedMilestone(-1);
      }
    });
  }, [progress]);

  const animatedProgressStyle = useAnimatedStyle(() => {
    let width = 0;
    if (layoutWidth && progressSV.value > 0) {
      let progressPx;
      if (progressSV.value >= 100) {
        progressPx = layoutWidth;
      } else {
        const offsetPx = (isMobile ? discountMilestoneOffsets.mobile.x : discountMilestoneOffsets.desktop.x) - 10;
        progressPx = (progressSV.value / 100) * layoutWidth;
        if (progressPx < 2 * offsetPx) {
          progressPx = progressPx / 2;
        } else {
          progressPx = progressPx - offsetPx;
        }
      }
      width = Math.ceil(progressPx);
    }
    return { width };
  });

  return {
    animatedProgressStyle,
    setLayoutWidth,
    steps,
    progressSV,
    lastReachedMilestone,
    anyMilestoneShowingCheckSV,
  };
};
